import createDOMPurify, { type DOMPurify } from 'dompurify';
import * as React from 'react';

import {
  ATTRIBUTES_BY_TAG,
  DROPPED_WITH_CONTENT,
  KEPT_CSS_PROPERTIES,
  KEPT_HREF,
  KEPT_IMAGE_ID,
  KEPT_TAGS,
  isHttpImage,
  isKeptImage,
  isOrgImage
} from './rich-text-allowlist';

/**
 * A stored rich-text value, drawn — an archived Salesforce `textarea` or a staff field's
 * `rich_text` (the advisor record page's roadmap, K4; the F1 kit plan's R7).
 *
 * **Every value goes through survey-api's allowlist first** (`rich-text-allowlist.ts`, a mirror):
 * the archive is served verbatim, and a staff field already clean comes out unchanged, so one path
 * serves both and none of them is trusted for being "ours". The sanitiser is a **private**
 * DOMPurify instance, so its hooks never reach another user of the library, and it runs once per
 * value: the render is memoised on the string, which the hub's five-second poll hands back
 * unchanged.
 *
 * Then three rules of the page's own, which are rendering and not sanitising — `sanitizeRichText`
 * beside it applies none of them, because the editor's loss check compares what survey-api would
 * store:
 *
 * - **(a) an org image is not loaded.** An `img` on Salesforce's `rtaImage` servlet becomes a muted
 *   "Image kept in Salesforce" (`[data-org-image]`): the page makes no request to the org, and the
 *   reader still sees an image is there. Once survey-api's block 8 has inlined those images as
 *   `data:`, they simply draw.
 * - **(b) a stored heading never becomes a page heading.** `h1`–`h6` are drawn as a `div` with
 *   `role="heading"` and `aria-level` raised by three, capped at 6 — an imported `<h1>` would
 *   otherwise break the page's one `h1`, which the suites hold, and the outline under it. A
 *   `div`, not a `p`: a stored heading may hold a block (`<h2><p>…</p></h2>`), and a `p` around
 *   it is closed by the parser when the markup is drawn, leaving an empty heading.
 * - **(c) a link opens beside the portal**, with `target="_blank"` and `rel="noopener noreferrer"`
 *   added at render; survey-api strips both, and this side never stores them.
 *
 * A value in which **no element survives the allowlist** — a plain archived `textarea` — is
 * drawn as text with its line breaks kept, which markup would otherwise collapse, and what is
 * drawn is **the stored string**, escaped by React, not the sanitised fragment's text: parsed as
 * HTML, a plain `From: Jane Doe <jane@firm.com>` loses the address as an unknown element and
 * `AT&amp;T` is decoded (F3's gate 2, ruled 2026-10-03). The price is that a value whose only
 * markup is refused (`<font>hi</font>`) shows its tags as text — verbatim, and inert.
 *
 * `dangerouslySetInnerHTML` is in this file and nowhere else in the tree; the alternative, a
 * React tree rebuilt from the DOM node by node, would be a second serialiser of the same
 * allowlist for no safety gained.
 */
export function RichText({
  html,
  className,
  serverResolvedImages = false
}: {
  html: string;
  className?: string;
  /**
   * Whether survey-api resolved this value's images and therefore already judged them.
   *
   * Set it for a value that came out of the content store (WEB-06 AC3) and nowhere else. The
   * server's rule there is **narrower** than this allowlist's: a body may hold a `data:` raster,
   * an org image, or a name of the site's own media library — and the name is what a URL in the
   * answer was resolved from. So an `http(s)` image in such a value is one of ours by
   * construction, while the same URL in an archived Salesforce value is a picture from wherever
   * that value points, which is why the default stays no.
   */
  serverResolvedImages?: boolean;
}) {
  const drawn = React.useMemo(
    () => renderRichText(html, serverResolvedImages),
    [html, serverResolvedImages]
  );
  const classes = ['rich-text', className].filter(Boolean).join(' ');
  return drawn.plain !== null ? (
    <div className={`${classes} whitespace-pre-line`}>{drawn.plain}</div>
  ) : (
    <div className={classes} dangerouslySetInnerHTML={{ __html: drawn.html }} />
  );
}

/**
 * `html` as survey-api's sanitiser would store it, and nothing more — none of the render rules.
 * The editor's loss check (R13) compares this with what the editor would write back.
 *
 * `serverResolvedImages` is `RichText`'s, and means the same thing: the value came from
 * survey-api, which already judged its images and answered the ones it keeps as URLs. Without
 * it a caller opening a stored body drops every picture in it — see `RichTextInput`.
 */
export function sanitizeRichText(html: string, serverResolvedImages = false): string {
  return serialize(sanitize(html, serverResolvedImages));
}

/**
 * Whether `html` holds an image on Salesforce's `rtaImage` servlet — which `RichText` draws as a
 * placeholder and the editor will not open (R13's read-only arm), since opening it would load the
 * image from the org.
 */
export function holdsOrgImage(html: string): boolean {
  return orgImages(sanitize(html)).length > 0;
}

function orgImages(fragment: DocumentFragment): HTMLImageElement[] {
  return Array.from(fragment.querySelectorAll('img')).filter((image) =>
    isOrgImage(image.getAttribute('src') ?? '')
  );
}

function renderRichText(
  html: string,
  serverResolvedImages = false
): { plain: Nullable<string>; html: string } {
  const fragment = sanitize(html, serverResolvedImages);
  if (fragment.querySelector('*') === null) {
    return { plain: html, html: '' };
  }
  const document = fragment.ownerDocument;

  for (const image of orgImages(fragment)) {
    const mark = document.createElement('span');
    mark.setAttribute('data-org-image', 'true');
    mark.textContent = 'Image kept in Salesforce';
    image.replaceWith(mark);
  }

  for (const heading of Array.from(fragment.querySelectorAll('h1, h2, h3, h4, h5, h6'))) {
    const level = Math.min(Number(heading.tagName.slice(1)) + 3, 6);
    const line = document.createElement('div');
    const style = heading.getAttribute('style');
    if (style) line.setAttribute('style', style);
    line.setAttribute('role', 'heading');
    line.setAttribute('aria-level', String(level));
    line.append(...Array.from(heading.childNodes));
    heading.replaceWith(line);
  }

  for (const link of Array.from(fragment.querySelectorAll('a'))) {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  }

  return { plain: null, html: serialize(fragment) };
}

function serialize(fragment: DocumentFragment): string {
  const box = fragment.ownerDocument.createElement('div');
  box.append(fragment);
  return box.innerHTML;
}

/**
 * Whether the value being sanitised right now had its images judged by survey-api.
 *
 * A module-scoped flag rather than an argument because DOMPurify's hooks belong to the instance
 * and take no caller state. It is correct for the same reason it looks wrong: `sanitize` is
 * synchronous, so nothing can run between setting it and clearing it — and it is cleared in a
 * `finally`, so a value that throws mid-parse cannot leave the next one trusted.
 */
let serverResolved = false;

function sanitize(html: string, serverResolvedImages = false): DocumentFragment {
  serverResolved = serverResolvedImages;
  try {
    return purify(html);
  } finally {
    serverResolved = false;
  }
}

function purify(html: string): DocumentFragment {
  return purifier().sanitize(html, {
    ALLOWED_TAGS: [...KEPT_TAGS],
    ALLOWED_ATTR: ['style', ...new Set(Object.values(ATTRIBUTES_BY_TAG).flat())],
    ALLOWED_URI_REGEXP: KEPT_HREF,
    FORBID_CONTENTS: [...DROPPED_WITH_CONTENT],
    KEEP_CONTENT: true,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    RETURN_DOM_FRAGMENT: true
  });
}

let instance: Nullable<DOMPurify> = null;

/** The private instance, made on first use with the three rules DOMPurify has no setting for. */
function purifier(): DOMPurify {
  if (instance) return instance;
  const purify = createDOMPurify(window);

  purify.addHook('uponSanitizeAttribute', (element, event) => {
    const tag = element.nodeName.toLowerCase();
    const name = event.attrName;
    if (name === 'style') {
      // Rebuilt from the declarations the browser parsed, so only a value valid for one of the
      // kept properties survives; nothing kept leaves no attribute.
      const kept = keptDeclarations((element as HTMLElement).style);
      if (kept) event.attrValue = kept;
      else event.keepAttr = false;
    } else if (!ATTRIBUTES_BY_TAG[tag]?.includes(name)) {
      event.keepAttr = false;
    } else if (tag === 'img' && name === 'data-image-id') {
      if (KEPT_IMAGE_ID.test(event.attrValue)) event.forceKeepAttr = true;
      else event.keepAttr = false;
    } else if (tag === 'img' && name === 'src') {
      // Q3's rule decides an image's source, not the link schemes: kept as written, or dropped
      // and the image with it below.
      if (isKeptImage(event.attrValue) || (serverResolved && isHttpImage(event.attrValue)))
        event.forceKeepAttr = true;
      else event.keepAttr = false;
    }
  });

  purify.addHook('afterSanitizeAttributes', (element) => {
    if (element.nodeName.toLowerCase() === 'img' && !element.hasAttribute('src')) {
      element.remove();
    }
  });

  instance = purify;
  return purify;
}

function keptDeclarations(style: CSSStyleDeclaration | undefined): string {
  if (!style) return '';
  const kept: string[] = [];
  for (let index = 0; index < style.length; index += 1) {
    const property = style.item(index);
    const value = style.getPropertyValue(property);
    // A shorthand's expansion fills the longhands it was not given with `initial` (Chromium turns
    // `text-decoration: underline` into four), which draws as the default anyway — and kept, it
    // would read to the editor's loss check (R13) as formatting the editor cannot hold.
    if (!KEPT_CSS_PROPERTIES.includes(property) || value === 'initial') continue;
    const priority = style.getPropertyPriority(property);
    kept.push(`${property}: ${value}${priority ? ' !important' : ''}`);
  }
  return kept.join('; ');
}
